import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from 'config/api';

// --- Card helpers (same behavior as AccountPurchase) ---
const detectCardBrand = (digits) => {
  if (/^3[47]\d{0,13}$/.test(digits)) return "amex"; // 34 or 37
  if (/^4\d{0,18}$/.test(digits)) return "visa";
  if (/^5[1-5]\d{0,14}$/.test(digits) || /^2(2[2-9]|[3-6]\d|7[01]|720)\d{0,12}$/.test(digits)) return "mastercard"; // incl. 2221–2720
  if (/^(5018|5020|5038|5612|5893|6304|6759|6761|6763)\d{0,15}$/.test(digits)) return "maestro";
  return "generic";
};

const formatCardNumber = (value) => {
  const digits = (value || "").replace(/\D/g, "");
  const brand = detectCardBrand(digits);
  let parts = [];
  if (brand === "amex") {
    parts = [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)];
  } else {
    parts = [
      digits.slice(0, 4),
      digits.slice(4, 8),
      digits.slice(8, 12),
      digits.slice(12, 16),
      digits.slice(16, 19),
    ];
  }
  return parts.filter(Boolean).join(" ").trim();
};

/**
 * SaveCard3DSDialog
 *
 * Props:
 *  - open: boolean
 *  - subscriptionId: string | number
 *  - onClose: () => void
 *  - onSuccess?: (payload: { paymentId?: string|null, cardToken?: string|null, cardUserKey?: string|null }) => void
 *
 * Usage:
 *  <SaveCard3DSDialog
 *    open={open}
 *    subscriptionId={subscriptionId}
 *    onClose={() => setOpen(false)}
 *    onSuccess={() => reloadUserData()}
 *  />
 */
export default function SaveCard3DSDialog({ open, subscriptionId, onClose, onSuccess }) {
  const [cardHolderName, setCardHolderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expireMonth, setExpireMonth] = useState("");
  const [expireYear, setExpireYear] = useState("");
  const [cvc, setCvc] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("form"); // "form" | "3ds"
  const [iframeHtml, setIframeHtml] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [paymentId, setPaymentId] = useState("");
  const [info, setInfo] = useState("");
  const [iframeLoading, setIframeLoading] = useState(false);
  const [successOverlay, setSuccessOverlay] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pollTimer = useRef(null);
  const pollDelayTimer = useRef(null);

  const iframeRef = useRef(null);

  // inject spinner keyframes once (match account-purchase modal)
  useEffect(() => {
    if (!document.getElementById("threeds-spinner-css")) {
      const style = document.createElement("style");
      style.id = "threeds-spinner-css";
      style.textContent = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }
  }, []);

  // detect if backend returned base64; if so decode, else return as-is
  const decode3DSHtml = (val = "") => {
    const s = String(val || "").trim();
    if (!s) return "";
    // if it already looks like HTML
    if (s.startsWith("<") || s.toLowerCase().startsWith("<!doctype")) return s;
    // otherwise try base64 decode
    try {
      return atob(s);
    } catch (_) {
      return s; // fallback: render raw
    }
  };

  const resetState = useCallback(() => {
    setCardHolderName("");
    setCardNumber("");
    setExpireMonth("");
    setExpireYear("");
    setCvc("");
    setSubmitting(false);
    setError("");
    setStep("form");
    setIframeHtml("");
    setSessionId("");
    setPaymentId("");
    setInfo("");
    setIframeLoading(false);
    setSuccessOverlay(false);
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    if (pollDelayTimer.current) {
      clearTimeout(pollDelayTimer.current);
      pollDelayTimer.current = null;
    }
  }, []);

  // Close handler merges cleanup + parent close
  const handleClose = useCallback(() => {
    resetState();
    onClose && onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  // Security: basic client-side masking/formatting (not required but UX nicety)
  const normalizedMonth = useMemo(() => (expireMonth || "").replace(/\D/g, "").slice(0, 2), [expireMonth]);
  const normalizedYear = useMemo(() => (expireYear || "").replace(/\D/g, "").slice(0, 4), [expireYear]);
  const normalizedCvc = useMemo(() => (cvc || "").replace(/\D/g, "").slice(0, 4), [cvc]);

  useEffect(() => setExpireMonth(normalizedMonth), [normalizedMonth]);
  useEffect(() => setExpireYear(normalizedYear), [normalizedYear]);
  useEffect(() => setCvc(normalizedCvc), [normalizedCvc]);

  // PostMessage listener for iyzico callback
  useEffect(() => {
    if (!open) return;

    const onMessage = (ev) => {
      const data = ev?.data || {};
      if (data?.type === "iyzico-3ds-save-card-done") {
        if (data.status === "success") {
          setInfo("Kart doğrulandı ve kaydedildi.");
          // stop polling
          if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
          }
          if (pollDelayTimer.current) {
            clearTimeout(pollDelayTimer.current);
            pollDelayTimer.current = null;
          }
          // show success overlay for 5s, then notify parent and close
          setSuccessOverlay(true);
          setTimeout(() => {
            onSuccess && onSuccess({
              paymentId: data.paymentId ?? null,
              cardToken: data.cardToken ?? null,
              cardUserKey: data.cardUserKey ?? null,
            });
            handleClose();
          }, 5000);
        } else {
          setError(data?.error || "3DS doğrulaması başarısız.");
          if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
          }
          if (pollDelayTimer.current) {
            clearTimeout(pollDelayTimer.current);
            pollDelayTimer.current = null;
          }
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, onSuccess, handleClose, setSuccessOverlay]);

  // Delayed fallback polling: wait ~75s for postMessage; then poll every 10s if still no result
  const startPolling = useCallback((sid) => {
    // clear any existing timers
    if (pollDelayTimer.current) {
      clearTimeout(pollDelayTimer.current);
      pollDelayTimer.current = null;
    }
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    // arm a delay first
    pollDelayTimer.current = setTimeout(() => {
      // safety: don't start if dialog closed
      if (!sid) return;
      // start low-frequency polling
      pollTimer.current = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/payment/check-3ds-status/${sid}`);
          if (!res.ok) return;
          const json = await res.json();
          if (json?.status === "success") {
            if (pollTimer.current) {
              clearInterval(pollTimer.current);
              pollTimer.current = null;
            }
            if (pollDelayTimer.current) {
              clearTimeout(pollDelayTimer.current);
              pollDelayTimer.current = null;
            }
            setInfo("Kart doğrulandı ve kaydedildi.");
            setSuccessOverlay(true);
            setTimeout(() => {
              onSuccess && onSuccess({
                paymentId: json.paymentId ?? null,
                cardToken: json.cardToken ?? null,
                cardUserKey: json.cardUserKey ?? null,
              });
              handleClose();
            }, 5000);
          } else if (json?.status === "failed") {
            if (pollTimer.current) {
              clearInterval(pollTimer.current);
              pollTimer.current = null;
            }
            if (pollDelayTimer.current) {
              clearTimeout(pollDelayTimer.current);
              pollDelayTimer.current = null;
            }
            setError(json?.error || "3DS doğrulaması başarısız.");
          }
        } catch (_) {
          // ignore transient network errors
        }
      }, 10000); // 10s interval
    }, 75000); // 75s delay before starting polling
  }, [onSuccess, handleClose]);

  const handleSubmit = useCallback(async (e) => {
    e && e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);

    try {
      if (!subscriptionId) {
        throw new Error("subscriptionId bulunamadı.");
      }

      const res = await fetch(`${API_BASE_URL}/payment/save-card-3ds`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`
        },
        body: JSON.stringify({
          subscriptionId,
          cardHolderName: cardHolderName?.trim(),
          cardNumber: cardNumber?.replace(/\s+/g, ""),
          expireMonth: String(expireMonth || "").padStart(2, "0"),
          expireYear: String(expireYear || ""),
          cvc: cvc
        }),
      });

      if (!res.ok) {
        const err = await safeJson(res);
        throw new Error(err?.error || "İstek başarısız.");
      }

      const json = await res.json();
      if (json?.status !== "3ds_required" || !json?.threeDSHtmlContent) {
        throw new Error("3DS başlatılamadı.");
      }

      setSessionId(json.sessionId);
      setPaymentId(json.paymentId || "");
      setIframeHtml(json.threeDSHtmlContent);
      setIframeLoading(true);
      setStep("3ds");
      setInfo("3D Secure doğrulaması bekleniyor...");

      // Start polling fallback
      if (json.sessionId) startPolling(json.sessionId);
    } catch (err) {
      setError(err?.message || "Beklenmeyen hata.");
    } finally {
      setSubmitting(false);
    }
  }, [subscriptionId, cardHolderName, cardNumber, expireMonth, expireYear, cvc, startPolling]);

  // When we enter the 3DS step, inject the (possibly base64) HTML into the iframe
  useEffect(() => {
    if (step !== "3ds") return;
    if (!iframeRef.current) return;
    const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
    if (!doc) return;
    const html = decode3DSHtml(iframeHtml);
    try {
      doc.open();
      setIframeLoading(true);
      doc.write(html);
      doc.close();
      try {
        if (iframeRef.current) {
          iframeRef.current.onload = () => setIframeLoading(false);
        }
      } catch (_) {}
      // fallback: hide loader after a short delay in case onload doesn't fire
      setTimeout(() => setIframeLoading(false), 500);
    } catch (_) {
      // ignore write errors
    }
  }, [step, iframeHtml]);

  // util to parse JSON safely
  async function safeJson(res) {
    try { return await res.json(); } catch { return null; }
  }

  if (!open) return null;

  return (
    <div style={styles.backdrop} onClick={handleClose}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        {step === "form" && (
          <div style={styles.header}>
            <div style={{ fontWeight: 600 }}>Kart Ekle (3DS ile)</div>
            <button onClick={handleClose} style={styles.closeBtn} aria-label="Kapat">×</button>
          </div>
        )}

        {step === "form" && (
          <form onSubmit={(e) => { e.preventDefault(); setConfirmOpen(true); }} style={styles.form}>
            {!!error && <div style={styles.error}>{error}</div>}
            {!!info && <div style={styles.info}>{info}</div>}

            <label style={styles.label}>
              Kart Üzerindeki İsim
              <input
                style={styles.input}
                autoFocus
                value={cardHolderName}
                onChange={(e) => setCardHolderName(e.target.value)}
                placeholder="AD SOYAD"
                required
                name="cc-name"
                autoComplete="cc-name"
              />
            </label>

            <label style={styles.label}>
              Kart Numarası
              <input
                style={styles.input}
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                onPaste={(e) => { const t = (e.clipboardData||window.clipboardData).getData('text')||''; e.preventDefault(); setCardNumber(formatCardNumber(t)); }}
                inputMode="numeric"
                maxLength={23}
                name="cc-number"
                autoComplete="cc-number"
                placeholder="5326 0000 0000 0000"
                required
              />
            </label>

            <div style={styles.row}>
              <label style={{ ...styles.label, flex: 1 }}>
                Ay (MM)
                <input
                  style={styles.input}
                  inputMode="numeric"
                  value={expireMonth}
                  onChange={(e) => {
                    const raw = (e.target.value||'').replace(/\D/g,'').slice(0,2);
                    let mm = raw;
                    if (raw.length === 1) {
                      if (raw === '0') { mm = ''; }
                      else if (parseInt(raw,10) > 1) { mm = '0'+raw; }
                    } else if (raw.length === 2) {
                      const n = parseInt(raw,10);
                      if (n === 0) mm = '';
                      else if (n > 12) mm = '12';
                      else mm = raw;
                    }
                    setExpireMonth(mm);
                  }}
                  onPaste={(e) => {
                    const text = (e.clipboardData||window.clipboardData).getData('text')||'';
                    const raw = text.replace(/\D/g,'').slice(0,2);
                    let mm = raw;
                    if (raw.length === 1) {
                      if (raw === '0') { mm = ''; }
                      else if (parseInt(raw,10) > 1) { mm = '0'+raw; }
                    } else if (raw.length === 2) {
                      const n = parseInt(raw,10);
                      if (n === 0) mm = '';
                      else if (n > 12) mm = '12';
                      else mm = raw;
                    }
                    e.preventDefault();
                    setExpireMonth(mm);
                  }}
                  name="cc-exp-month"
                  autoComplete="cc-exp-month"
                  placeholder="MM"
                  required
                />
              </label>
              <label style={{ ...styles.label, flex: 1 }}>
                Yıl (20YY)
                <input
                  style={styles.input}
                  inputMode="numeric"
                  value={expireYear}
                  onChange={(e)=> setExpireYear(((e.target.value||'').replace(/\D/g,'').slice(0,2)))}
                  onPaste={(e)=>{ const t=(e.clipboardData||window.clipboardData).getData('text')||''; e.preventDefault(); setExpireYear(t.replace(/\D/g,'').slice(0,2)); }}
                  maxLength={2}
                  name="cc-exp-year"
                  autoComplete="cc-exp-year"
                  placeholder="YY"
                  required
                />
              </label>
              <label style={{ ...styles.label, flex: 1 }}>
                CVC
                <input
                  style={styles.input}
                  inputMode="numeric"
                  value={cvc}
                  onChange={(e) => setCvc(e.target.value)}
                  placeholder="CVC"
                  required
                  name="cc-csc"
                  autoComplete="cc-csc"
                  maxLength={3}
                />
              </label>
            </div>

            <div style={styles.actions}>
              <button type="button" onClick={handleClose} style={styles.secondaryBtn}>Vazgeç</button>
              <button type="button" disabled={submitting} style={styles.primaryBtn} onClick={() => setConfirmOpen(true)}>
                {submitting ? "Başlatılıyor…" : "3DS ile Kaydet"}
              </button>
            </div>
          </form>
        )}

        {step === "3ds" && (
          <div style={styles.threeDSBackdrop} onClick={handleClose}>
            <div style={styles.threeDSModal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.threeDSHeader}>
                <h3 style={{ margin: 0, color: "#333" }}>3D Secure Doğrulama</h3>
                <button onClick={handleClose} style={styles.threeDSCloseBtn}>&times;</button>
              </div>
              <div style={styles.threeDSBody}>
                <iframe
                  ref={iframeRef}
                  title="3DS"
                  sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
                {iframeLoading && (
                  <div style={styles.threeDSLoading}>
                    <div style={styles.threeDSSpinner}></div>
                    <span>3D Secure yükleniyor...</span>
                  </div>
                )}
                {successOverlay && (
                  <div style={styles.threeDSSuccess}>
                    <div style={styles.threeDSSuccessIcon}>✓</div>
                    <div style={styles.threeDSSuccessText}>
                      Kart bilgileriniz kaydedilmiştir.<br/>Bu pencere 5 saniye içinde kapanacaktır.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {confirmOpen && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Kart Doğrulama Bilgilendirmesi</h3>
              <p style={{ fontSize: 14, lineHeight: "20px", color: "#333", marginTop: 10 }}>
                Kart doğrulaması ve güvenliği adına hesabınızdan geçici olarak <b>1 TL</b> çekilecektir.
                İşlem sonunda bu tutar otomatik olarak iade edilecektir.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button style={styles.secondaryBtn} onClick={() => setConfirmOpen(false)}>İptal</button>
                <button
                  style={styles.primaryBtn}
                  onClick={() => {
                    setConfirmOpen(false);
                    handleSubmit();
                  }}
                >
                  Kabul Ediyorum
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  dialog: {
    width: "min(720px, 96vw)",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    padding: 16,
    maxHeight: "90vh",
    overflow: "auto",
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: 20,
    lineHeight: 1,
    cursor: "pointer",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  label: {
    display: "flex",
    flexDirection: "column",
    fontSize: 13,
    gap: 6,
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
  },
  row: {
    display: "flex",
    gap: 12,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  primaryBtn: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid #222",
    background: "#222",
    color: "#fff",
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid #ddd",
    background: "#fff",
    color: "#111",
    cursor: "pointer",
  },
  error: {
    background: "#fff3f3",
    color: "#b30000",
    border: "1px solid #ffd6d6",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 13,
  },
  info: {
    background: "#f6f9ff",
    color: "#0b3d91",
    border: "1px solid #d7e3ff",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 13,
  },
  threeDSBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  threeDSModal: {
    background: "white",
    width: "90%",
    maxWidth: "500px",
    height: "600px",
    borderRadius: "10px",
    position: "relative",
    overflow: "hidden",
  },
  threeDSHeader: {
    padding: "15px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  threeDSCloseBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#999",
  },
  threeDSBody: {
    height: "calc(100% - 60px)",
    position: "relative",
  },
  threeDSLoading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#666",
  },
  threeDSSpinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "10px",
  },
  loadingOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#666",
    gap: 8,
    userSelect: "none",
    pointerEvents: "none",
  },
  loadingSpinner: {
    width: 40,
    height: 40,
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 10,
  },
  threeDSSuccess: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "16px 18px",
    textAlign: "center",
    boxShadow: "0 10px 24px rgba(0,0,0,0.15)",
    zIndex: 2,
  },
  threeDSSuccessIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    border: "2px solid #22c55e",
    color: "#22c55e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 24,
  },
  threeDSSuccessText: {
    color: "#111827",
    fontSize: 14,
    lineHeight: "20px",
  },confirmOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  confirmCard: {
    background: "#fff",
    padding: 20,
    borderRadius: 10,
    width: "min(420px, 92vw)",
    boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
  },
};
  
